import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Wand2, Loader2, Sparkles, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";

/**
 * "Make every saree Reel-ready" — bulk AI on-model generation dialog.
 *
 * - Calls /api/admin/sarees/bulk-ai-model/preview to show how many sarees need
 *   an AI photo + how many fit inside today's quota.
 * - On Confirm, posts /admin/sarees/bulk-ai-model, then polls progress every 3s.
 * - Lets the parent (ShopAdmin) refresh once everything is done.
 */
export default function BulkAIDialog({ onAfterDone }) {
    const [open, setOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [starting, setStarting] = useState(false);
    const [jobIds, setJobIds] = useState(null);
    const [progress, setProgress] = useState(null);
    const pollRef = useRef(null);

    const loadPreview = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/sarees/bulk-ai-model/preview");
            setPreview(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
        } finally { setLoading(false); }
    };

    useEffect(() => {
        if (open) {
            setJobIds(null); setProgress(null);
            loadPreview();
        } else if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, [open]);

    const startBulk = async () => {
        setStarting(true);
        try {
            const { data } = await api.post("/admin/sarees/bulk-ai-model", {
                style: "modern", skin_tone: "medium", only_missing: true,
            });
            setJobIds(data.job_ids);
            setProgress({ total: data.queued, done: 0, running: data.queued, failed: 0, errors: [] });
            toast.success(`Queued ${data.queued} ${data.queued === 1 ? "saree" : "sarees"}${data.byok ? " on your Gemini key" : ""}`);
            if (data.skipped_quota > 0) {
                toast.warning(`${data.skipped_quota} skipped — daily quota hit. Add your Gemini key in Settings for unlimited.`);
            }
            // poll
            const ids = (data.job_ids || []).join(",");
            const tick = async () => {
                try {
                    const { data: p } = await api.get(`/admin/sarees/bulk-ai-model/progress?job_ids=${ids}`);
                    setProgress(p);
                    if (p.done + p.failed >= p.total) {
                        clearInterval(pollRef.current); pollRef.current = null;
                        toast.success(`All done · ${p.done} success${p.failed ? `, ${p.failed} failed` : ""}`);
                        onAfterDone?.();
                    }
                } catch { /* keep trying */ }
            };
            tick();
            pollRef.current = setInterval(tick, 3000);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk generation failed");
        } finally { setStarting(false); }
    };

    const pct = progress ? Math.round(((progress.done + progress.failed) / Math.max(1, progress.total)) * 100) : 0;

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button className="ayraa-btn-outline" data-testid="bulk-ai-btn">
                    <Wand2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> AI photos for all
                </button>
            </DialogTrigger>
            <DialogContent className="bg-brand-paper border-brand-border max-w-lg max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-editorial">
                        Make every saree <em className="not-italic text-brand-bronze">Reel-ready</em>
                    </DialogTitle>
                </DialogHeader>

                {loading || !preview ? (
                    <div className="space-y-3 py-4">
                        <div className="h-24 skel rounded-sm" />
                        <div className="h-12 skel rounded-sm" />
                    </div>
                ) : !progress ? (
                    <div className="space-y-5" data-testid="bulk-ai-preview">
                        {/* count card */}
                        <div className="border border-brand-border bg-brand-paper p-5 rounded-sm flex items-start gap-4">
                            <div className="w-10 h-10 grid place-items-center bg-brand-ink text-brand-paper rounded-sm shrink-0">
                                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="ayraa-overline">Sarees missing AI photo</div>
                                <div className="font-display text-4xl mt-1 tracking-editorial" data-testid="bulk-ai-pending">
                                    {preview.pending_count}
                                </div>
                                {preview.pending_count > 0 ? (
                                    <p className="text-brand-muted text-sm mt-2 leading-relaxed">
                                        We&apos;ll generate one editorial on-model photo per saree using Gemini.
                                        Each one becomes part of your customer-facing catalog and Reel-mode swipes.
                                    </p>
                                ) : (
                                    <p className="text-brand-muted text-sm mt-2">Every saree already has an AI photo. Nothing to do.</p>
                                )}
                            </div>
                        </div>

                        {/* quota */}
                        {preview.pending_count > 0 ? (
                            <div className="border border-brand-border bg-brand-subtle p-4 rounded-sm">
                                <div className="flex items-center justify-between">
                                    <div className="ayraa-overline">Today&apos;s AI budget</div>
                                    <div className="font-mono text-xs">
                                        {preview.quota.byok ? (
                                            <span className="text-emerald-700">Unlimited · your key</span>
                                        ) : (
                                            <span>{preview.quota.used} / {preview.quota.limit}</span>
                                        )}
                                    </div>
                                </div>
                                {!preview.quota.byok ? (
                                    <>
                                        <div className="mt-3 h-1.5 bg-brand-border overflow-hidden">
                                            <div className="h-full bg-brand-bronze transition-all"
                                                style={{ width: `${Math.min(100, Math.round(preview.quota.used / Math.max(1, preview.quota.limit) * 100))}%` }} />
                                        </div>
                                        {preview.can_generate_now < preview.pending_count ? (
                                            <p className="mt-3 text-xs text-brand-muted">
                                                Only <span className="font-mono text-brand-ink">{preview.can_generate_now}</span> will be processed
                                                today; the remaining <span className="font-mono text-brand-ink">{preview.pending_count - preview.can_generate_now}</span> will
                                                be skipped. Add your Gemini key in Settings for no limit.
                                            </p>
                                        ) : null}
                                    </>
                                ) : null}
                            </div>
                        ) : null}

                        {/* sample titles */}
                        {preview.pending.length > 0 ? (
                            <div className="text-xs text-brand-muted">
                                Will process: <span className="font-mono text-brand-ink">
                                    {preview.pending.slice(0, 5).map((s) => s.title).join(", ")}
                                </span>{preview.pending.length > 5 ? `, +${preview.pending.length - 5} more…` : ""}
                            </div>
                        ) : null}

                        <button
                            className="ayraa-btn-primary w-full"
                            disabled={starting || preview.pending_count === 0 || preview.can_generate_now === 0}
                            onClick={startBulk}
                            data-testid="bulk-ai-start"
                        >
                            {starting ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</>
                            ) : preview.pending_count === 0 ? (
                                "Nothing to do"
                            ) : preview.can_generate_now === 0 ? (
                                <><AlertTriangle className="w-4 h-4 mr-2" /> Daily quota reached</>
                            ) : (
                                <><Wand2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Generate {preview.can_generate_now} {preview.can_generate_now === 1 ? "photo" : "photos"}</>
                            )}
                        </button>
                    </div>
                ) : (
                    /* live progress */
                    <div className="space-y-5" data-testid="bulk-ai-progress">
                        <div className="border border-brand-border bg-brand-paper p-5 rounded-sm">
                            <div className="flex items-baseline justify-between">
                                <div>
                                    <div className="ayraa-overline">Generating…</div>
                                    <div className="font-display text-4xl mt-1 tracking-editorial">
                                        <span className="font-mono">{progress.done + progress.failed}</span>
                                        <span className="text-brand-soft"> / </span>
                                        <span className="font-mono">{progress.total}</span>
                                    </div>
                                </div>
                                <div className="font-mono text-sm text-brand-bronze">{pct}%</div>
                            </div>
                            <div className="mt-4 h-1.5 bg-brand-border overflow-hidden">
                                <div className="h-full bg-brand-bronze transition-all duration-500"
                                    style={{ width: `${pct}%` }} data-testid="bulk-ai-bar" />
                            </div>
                            <div className="mt-4 flex items-center gap-5 font-mono text-[11px] uppercase tracking-wider-2">
                                <span className="flex items-center gap-1.5 text-emerald-700">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> {progress.done} done
                                </span>
                                {progress.running > 0 ? (
                                    <span className="flex items-center gap-1.5 text-brand-muted">
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> {progress.running} running
                                    </span>
                                ) : null}
                                {progress.failed > 0 ? (
                                    <span className="flex items-center gap-1.5 text-red-700">
                                        <XCircle className="w-3.5 h-3.5" /> {progress.failed} failed
                                    </span>
                                ) : null}
                            </div>
                        </div>

                        <p className="text-xs text-brand-soft text-center">
                            You can close this dialog — generation continues in the background.
                        </p>

                        <button
                            className="ayraa-btn-outline w-full"
                            onClick={() => setOpen(false)}
                            data-testid="bulk-ai-close"
                        >
                            Close
                        </button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
